import { getDefaultDataForType } from '../../pages/LabelCreator/utils/contentDefaults';
import { getGIInspectionData } from '../../utils/certificate';
import { CORE_METRICS, isProductCoreData, readCoreMetric, getProductCoreDefaults, coreEditorFields } from '../../utils/productCore';

export const GI_COMPONENTS = ['gi-product', 'image', 'product-basic', 'product-core', 'nutrition', 'usage', 'inspection', 'related-links'];
export const PRODUCT_FIELDS = [['productName','产品名称'],['ingredients','配料表'],['netContent','净含量 / 规格'],['standardCode','产品标准代号'],['manufacturer','生产商'],['address','生产地址'],['origin','产地'],['productionDate','生产日期'],['shelfLife','保质期'],['storageConditions','贮存条件'],['productionLicense','食品生产许可证编号'],['serviceHotline','服务热线']];
const CUSTOM_FIELDS = [['brand','品牌名称'],['barcode','商品条码'],['companyName','企业名称'],['companyAddress','企业地址'],['creditCode','统一社会信用代码'],['batchNumber','批次号'],['expiryDate','有效期至'],['batchQuantity','该批次数量']];
export const readItem = item => {
  const raw = item?.contentData ?? item?.data;
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return {}; } }
  return raw || {};
};
export const itemType = item => item.contentType || item.type;
export const isProductCoreItem = item => itemType(item) === 'product-core' || (itemType(item) === 'product-basic' && isProductCoreData(readItem(item)));
// Core data keeps the original product information JSON/API format.
export const giStorageType = type => type === 'product-core' ? 'product-basic' : type;
export const findGIModule = (items, type) => items.find(item => type === 'product-core' ? isProductCoreItem(item) : itemType(item) === type && (type !== 'product-basic' || !isProductCoreItem(item)));

// Only adds missing blocks in the local editor. Existing records are never rewritten on read.
export function ensureGIContentItems(items) {
  const gi = resolveGIContent({ contentItems: items }).gi;
  const missing = GI_COMPONENTS.filter(type => !findGIModule(items, type));
  const next = [...items, ...missing.map((type, i) => {
    const data = getGIModuleDefaults(type, gi);
    return { id: Date.now() + items.length + i, type: giStorageType(type), data };
  })];
  // Move only the duplicate fields introduced by our earlier GI templates.
  // The source record remains untouched; persistence happens on explicit save.
  return next.map(item => {
    if (itemType(item) !== 'product-basic' || isProductCoreItem(item)) return item;
    const data = readItem(item);
    const duplicateIds = ['gi-gi', 'gi-gl'];
    if (!(data.customFields || []).some(field => duplicateIds.includes(field._id))) return item;
    const updated = { ...data, customFields: data.customFields.filter(field => !duplicateIds.includes(field._id)) };
    if (data.fieldOrder) updated.fieldOrder = data.fieldOrder.filter(id => !duplicateIds.includes(id));
    return item.contentData !== undefined
      ? { ...item, contentData: typeof item.contentData === 'string' ? JSON.stringify(updated) : updated }
      : { ...item, data: updated };
  });
}

export function getGIModuleDefaults(type, gi = {}) {
  if (type === 'product-core') return getProductCoreDefaults(gi);
  let data = JSON.parse(JSON.stringify(getDefaultDataForType(type)));
  if (type === 'image') data = { ...data, images: (gi.images || []).map(src => typeof src === 'string' ? { src, width: 100 } : src), carousel: { ...data.carousel, enabled: true } };
  if (type === 'product-basic') {
    PRODUCT_FIELDS.forEach(([key]) => { data[key] = gi[key === 'serviceHotline' ? 'hotline' : key] ?? ''; });
    data.customFields = CUSTOM_FIELDS.map(([key,label]) => ({ _id: `gi-${key}`, label, value: gi[key] ?? '' }));
  }
  if (type === 'related-links') data.links = [{ enabled: true, name: gi.websiteName || '官方网站', url: gi.websiteUrl || '', icon: gi.websiteLogo || '', order: 1 }];
  if (type === 'inspection') data = getGIInspectionData(gi.reportUrl);
  return data;
}

export function productEntries(product) {
  const fields = isProductCoreData(product) ? coreEditorFields(product).map(field => [field.key, field.label]) : PRODUCT_FIELDS;
  const entries = [...fields.map(([key,label]) => ({ id:key, label, value:product[key] })), ...(product.customFields || []).map((field,i) => ({ id:field._id || `custom-${i}`, label:field.label, value:field.value }))];
  const order = product.fieldOrder || entries.map(entry => entry.id);
  return [...order.map(id => entries.find(entry => entry.id === id)).filter(Boolean), ...entries.filter(entry => !order.includes(entry.id))].filter(entry => entry.value !== '' && entry.value != null);
}

export function resolveGIContent(data) {
  const items = data.contentItems || [];
  const blocks = type => items.filter(item => itemType(item) === type).map(readItem);
  const gi = { ...(blocks('gi-product')[0] || {}) };
  const products = items.filter(item => itemType(item) === 'product-basic' || isProductCoreItem(item)).map(item => isProductCoreItem(item) ? { ...readItem(item), moduleRole: 'product-core' } : readItem(item));
  const basicProducts = products.filter(product => !isProductCoreData(product));
  const coreProducts = products.filter(isProductCoreData);
  const entries = basicProducts.flatMap(productEntries);
  const find = (...labels) => entries.find(entry => labels.includes(entry.label))?.value;
  if (basicProducts.length) {
    PRODUCT_FIELDS.forEach(([key]) => { gi[key === 'serviceHotline' ? 'hotline' : key] = basicProducts.map(product => product[key]).find(value => value !== '' && value != null) ?? ''; });
    CUSTOM_FIELDS.forEach(([key,label]) => { gi[key] = entries.find(entry => entry.id === `gi-${key}`)?.value ?? find(label) ?? ''; });
  }
  for (const metric of CORE_METRICS) {
    // An existing core block is authoritative, including intentionally cleared values.
    // Fall back only for older labels that do not yet contain a core block.
    gi[metric.key] = coreProducts.length
      ? coreProducts.map(product => readCoreMetric(product, metric)).find(value => value !== undefined) ?? ''
      : basicProducts.length
        ? basicProducts.map(product => readCoreMetric(product, metric)).find(value => value !== undefined) ?? ''
        : gi[metric.key] ?? '';
  }
  const imageBlocks = blocks('image');
  if (imageBlocks.length) gi.images = imageBlocks.flatMap(block => block.images || []).map(image => typeof image === 'string' ? image : image.src || image.url).filter(Boolean);
  const links = blocks('related-links').flatMap(block => block.links || []).filter(link => link.enabled !== false && link.url).sort((a,b) => (a.order || 0) - (b.order || 0));
  if (blocks('related-links').length) {
    gi.websiteName = links[0]?.name || ''; gi.websiteUrl = links[0]?.url || ''; gi.websiteLogo = links[0]?.icon || '';
  }
  return { gi, products, imageDescriptions: imageBlocks.map(block => block.description).filter(Boolean), imageOptions: imageBlocks[0]?.carousel || {}, links, usages: blocks('usage'), extras: items.filter(item => !GI_COMPONENTS.includes(itemType(item)) && itemType(item) !== 'background') };
}
